import {Circle as CircleStyle, Fill, Stroke, Style, Text} from 'ol/style';
import {Draw, Modify} from 'ol/interaction';
import {LineString, Point} from 'ol/geom';
import {Coordinate as olCoordinate} from "ol/coordinate";
import {Feature, MapBrowserEvent} from "ol";
import {asArray} from "ol/color";
import {fromLonLat, toLonLat} from "ol/proj";
import ShapeCoordinateMutationAction from "../ActionHistory/ShapeCoordinateMutationAction.js";
import {Serialization} from "./Serializer";
import {jsPDF} from "jspdf";
import Cutout from "./Cutout";
import {
    diff,
    Point as MPoint,
} from "../Util/Math";
import {
    altKeyOnly,
    noModifierKeys,
    platformModifierKeyOnly,
    primaryAction, shiftKeyOnly,
    singleClick
} from "ol/events/condition";
import $ from "jquery";
import {computed, reactive} from "vue";
import ShapeCollection from "./ShapeCollection.js";
import * as olExtent from "ol/extent.js";
import {getArea, getLength} from "ol/sphere.js";
import {Polygon} from "ol/geom.js";
import Route from "./Route.js";
import {unreactive} from "../Util/functions.js";
import * as polyclip from "polyclip-ts";
import CoordinateConverter from "../Util/CoordinateConverter.js";
import ConversionComposition from "../Conversion/ConversionComposition.js";
import {WGS84System} from "../Coordinates/WGS84.js";

export type LineStyleType = 'none'|'solid'|'dashed'|'dotted';

export type ShapeReactiveProps = {
    type: 'shape',
    id: number,
    name: string,
    color: string,
    isCustomColor: boolean,
    visibleOnMap: boolean,
    lineStyleType: LineStyleType,
    fillInterior: boolean,
    quickEditingName: boolean,
    hasFocus: boolean,
    drawMode: ShapeDrawMode|null,
    formattedArea: string,
};

export type ShapeDrawMode = 'remove-coordinate'|'freehand';

export default class Shape {

    static idIncrement: number = 0;

    reactiveProps: ShapeReactiveProps;

    readonly id: number;

    private oldCoordinates: olCoordinate[] = [];

    private name = null;
    private color = null;

    private singlePointStyle;
    private polygonStyles;
    private modifyStyle;

    private modifyInteraction;
    private drawInteraction;
    private mainFeature: Feature<any>;

    private visibleOnMap = true;
    private hasMouseover = false;

    private lineStyleType:LineStyleType = 'none';
    private fillInterior:boolean = true;

    private modifyDragging = false;

    constructor(readonly shapeCollection: ShapeCollection) {
        this.id = Shape.idIncrement++;

        this.reactiveProps = <ShapeReactiveProps>reactive({
            type: 'shape',
            id: this.id,
            name: this.name,
            color: this.color,
            isCustomColor: computed(() => unreactive(this.shapeCollection.userInterface.colors.indexOf(this.reactiveProps.color) == -1)),
            visibleOnMap: this.visibleOnMap,
            lineStyleType: this.lineStyleType,
            fillInterior: this.fillInterior,
            quickEditingName: false,
            hasFocus: false,
            drawMode: null,
            formattedArea: '',
        });

        this.initStyles();
        this.initInteraction();

        this.updateReactiveProps();
    }

    private initStyles() {
        const workspaceShapeConfig = this.shapeCollection.getWorkspaceShapeConfig();
        const baseAlpha = workspaceShapeConfig.baseAlpha;
        let baseWidth = workspaceShapeConfig.baseWidth;
        let fixWidthFromZoom = workspaceShapeConfig.fixWidthFromZoom;
        const strokeWhite = workspaceShapeConfig.strokeWhite;
        const zoom = this.shapeCollection.getOpenlayersMap().getView().getZoom();
        if (fixWidthFromZoom !== null && zoom > fixWidthFromZoom) {
            baseWidth = Math.min(baseWidth * 2 ** (zoom - fixWidthFromZoom), this.hasFocus() ? 8 : 16);
        }
        const multiplier = baseWidth / workspaceShapeConfig.baseWidth;

        const active = this.hasMouseover || this.hasFocus();

        const color = asArray(this.color || '#000000').slice(0);
        color[3] = active ? (baseAlpha + 0.3) : (baseAlpha + 0.1);

        this.polygonStyles = [];

        const lineDash = this.lineStyleType === 'dashed'
            ? [2 * Math.sqrt(multiplier) * 5, 2 * Math.sqrt(multiplier) * 5]
            : this.lineStyleType === 'dotted' ? [2 * Math.sqrt(multiplier), 2 * multiplier * 2 + 1] : null;

        const strokeColor = strokeWhite ? '255' : '0';

        // Shape outer/stroke styling
        if (this.lineStyleType !== 'none') {
            this.polygonStyles.push(new Style({
                stroke: new Stroke({
                    color: 'rgba(' + strokeColor + ', ' + strokeColor + ', ' + strokeColor + ', ' + (active ? (baseAlpha + 0.2) : baseAlpha) + ')',
                    width: active ? (baseWidth + 3) : (baseWidth + 2),
                    lineDash: lineDash,
                }),
            }));
        }

        // Shape inner styling and cursor circle
        this.polygonStyles.push(new Style({
            fill: this.fillInterior ? new Fill({
                color: [color[0], color[1], color[2], Math.max(0.1, color[3] - 0.3)],
            }) : undefined,
            stroke: new Stroke({
                color: color,
                width: active ? (baseWidth + 1) : baseWidth,
                lineDash: lineDash,
            }),
            image: new CircleStyle({
                radius: 5,
                stroke: new Stroke({
                    color: 'rgba(0, 0, 0, 0.7)',
                }),
                fill: new Fill({
                    color: 'rgba(255, 255, 255, 0.2)',
                }),
            }),
        }));

        // Style when shape exists of only 1 coordinate
        this.singlePointStyle = new Style({
            image: new CircleStyle({
                radius: active ? (baseWidth + 1) : baseWidth,
                stroke: new Stroke({
                    color: 'rgba(' + strokeColor + ', ' + strokeColor + ', ' + strokeColor + ', ' + (active ? (baseAlpha + 0.2) : baseAlpha) + ')',
                    width: 1,
                }),
                fill: new Fill({
                    color: color,
                }),
            }),
        });

        // Style of the circle that snaps to the shape when modifying
        this.modifyStyle = new Style({
            image: new CircleStyle({
                radius: baseWidth/2 + 4,
                stroke: new Stroke({
                    color: 'rgba(0, 0, 0, 0.7)',
                }),
                fill: new Fill({
                    color: 'rgba(0, 0, 0, 0.4)',
                }),
            }),
            text: new Text({
                font: '12px Calibri,sans-serif',
                fill: new Fill({
                    color: 'rgba(255, 255, 255, 1)',
                }),
                backgroundFill: new Fill({
                    color: 'rgba(0, 0, 0, 0.7)',
                }),
                padding: [2, 2, 2, 2],
                textAlign: 'left',
                offsetX: 15,
            }),
        });
    }

    private initInteraction() {
        const shapeCollection = this.shapeCollection;

        const styleFunction = (feature) => {
            if (!this.visibleOnMap) {
                return [new Style({})];
            }

            const styles = this.polygonStyles.slice()

            if (this.getCoordinates().length === 1) {
                this.singlePointStyle.setGeometry(new Point(this.getCoordinates()[0]));

                styles.push(this.singlePointStyle);
            }

            return styles;
        }

        const self = this;

        this.drawInteraction = new class extends Draw {
            constructor() {
                super({
                    source: shapeCollection.drawSource,
                    type: 'Polygon',
                    style: function (feature) {
                        return styleFunction(feature);
                    },
                    stopClick: true,
                    condition: (e: MapBrowserEvent<any>) => {
                        // Default: noModifierKeys()
                        let condition;
                        if (self.reactiveProps.drawMode === 'remove-coordinate') {
                            condition = altKeyOnly(e);
                        } else if (self.reactiveProps.drawMode === 'freehand') {
                            condition = shiftKeyOnly(e);
                        } else {
                            condition = noModifierKeys(e) || platformModifierKeyOnly(e);
                        }
                        return condition && primaryAction(e);
                    },
                    freehandCondition: (e: MapBrowserEvent<any>) => {
                        // Default: shiftKeyOnly()
                        if (self.reactiveProps.drawMode === 'freehand') {
                            return !shiftKeyOnly(e) && !altKeyOnly(e);
                        } else {
                            return shiftKeyOnly(e);
                        }
                    },
                });
            }

            handleUpEvent(event) {
                if (self.modifyDragging) {
                    // Fix for bug when modifying the shape fast over a short distance+time
                    return true;
                }

                const pass = super.handleUpEvent(event);

                if (pass) {
                    return true;
                }

                this.finishDrawing();

                const geometry = <Polygon>shapeCollection.drawSource.getFeatures()[0].getGeometry();

                const newCoordinates = <olCoordinate[][]>geometry.getCoordinates();
                const coordinates = polygon.getCoordinates();
                const changed = Shape.appendPolygonCoordinates(coordinates, newCoordinates);
                polygon.setCoordinates(coordinates);

                shapeCollection.drawSource.clear();

                self.triggerChanged(changed);

                return false;
            }
        };

        const polygon = new Polygon([]);
        this.mainFeature = new Feature({
            geometry: polygon,
            shape: this,
        });
        this.mainFeature.setStyle(styleFunction);
        this.modifyInteraction = new Modify({
            source: shapeCollection.modifySource,
            style: () => this.modifyStyle,
            deleteCondition: (e: MapBrowserEvent<any>) => {
                // Default: singleClick() && altKeyOnly()
                const condition = singleClick(e) && altKeyOnly(e);
                if (this.reactiveProps.drawMode === 'remove-coordinate') {
                    return !condition;
                } else {
                    return condition;
                }
            }
        });

        this.modifyInteraction.on('modifystart', () => {
            this.modifyDragging = true;
        });

        this.modifyInteraction.on('modifyend', () => {
            this.triggerChanged(true);
            this.modifyDragging = false;
        });

        //draw.on('drawstart', (evt) => {
        //    evt.feature.setStyle(styleFunction);
        //});
    }

    public static appendPolygonCoordinates(coordinates: olCoordinate[][], newCoordinates: olCoordinate[][]): boolean
    {
        if (newCoordinates.length === 0) {
            return false;
        }

        let newCoords = newCoordinates[0];

        if (coordinates.length === 0) {
            coordinates.push([]);
        }
        const coords = coordinates[0];

        if (
            newCoords.length > 1
            && newCoords[0][0] === newCoords[newCoords.length - 1][0]
            && newCoords[0][1] === newCoords[newCoords.length - 1][1]
        ) {
            // Incoming from freeform, remove double start/end coordinate
            newCoords = newCoords.slice(0, -1);
        }

        let changed = false;

        for (const newCoordinate of newCoords) {
            const lastCoordinate = coords[coords.length - 1];

            if (!lastCoordinate || newCoordinate[0] !== lastCoordinate[0] || newCoordinate[1] !== lastCoordinate[1]) {
                if (coords.length === 0) {
                    coords.push(newCoordinate); // Start coordinate
                    coords.push(newCoordinate); // End coordinate
                } else {
                    coords.splice(-1, 0, newCoordinate);
                }
                changed = true;
            }
        }

        return changed;
    }

    public addToMap()
    {
        this.shapeCollection.mainSource.addFeature(this.mainFeature);
    }

    public removeFromMap()
    {
        this.mouseout(false);
        this.shapeCollection.mainSource.removeFeature(this.mainFeature);
    }

    public triggerChanged(addAction)
    {
        this.updateReactiveProps();

        if (addAction) {
            this.shapeCollection.userInterface.actionHistory.addAction(
                new ShapeCoordinateMutationAction(this, this.oldCoordinates)
            );
        }
        this.oldCoordinates = this.getCoordinates().slice();
    }

    public getOldCoordinates(): olCoordinate[]
    {
        return this.oldCoordinates.slice();
    }

    public getCoordinates(): olCoordinate[]
    {
        let coordinates = this.mainFeature.getGeometry().getCoordinates()[0] || [];
        if (coordinates.length > 0) {
            coordinates = coordinates.slice(0, -1);
        }
        return coordinates;
    }

    public getGeometry()
    {
        return this.mainFeature.getGeometry();
    }

    public getName()
    {
        return this.name;
    }

    public getColor()
    {
        return this.color;
    }

    public setCoordinates(coordinates: olCoordinate[], addAction: boolean = false)
    {
        coordinates = coordinates.slice();
        if (coordinates.length > 0) {
            coordinates.push(coordinates[0]);
        }
        this.mainFeature.getGeometry().setCoordinates([coordinates]);
        this.triggerChanged(addAction);
    }

    public applyCoordinatesChange(coordinates: olCoordinate[], mutationIndex: number, mutationAmount: number)
    {
        this.setCoordinates(coordinates);
    }

    public setColor(color: null|string)
    {
        this.reactiveProps.color = this.color = color;
        this.initStyles();
        this.mainFeature.changed();
    }

    public setName(name: string)
    {
        this.reactiveProps.name = this.name = name;
    }

    public getLineStyleType(): LineStyleType
    {
        return this.lineStyleType;
    }

    public setLineStyleType(lineStyleType: LineStyleType)
    {
        this.reactiveProps.lineStyleType = this.lineStyleType = lineStyleType;
        this.initStyles();
        this.mainFeature.changed();
    }

    public getFillInterior(): boolean
    {
        return this.fillInterior;
    }

    public setFillInterior(fillInterior: boolean)
    {
        this.reactiveProps.fillInterior = this.fillInterior = fillInterior;
        this.initStyles();
        this.mainFeature.changed();
    }

    public updateWorkspaceShapeConfig(): void
    {
        this.initStyles();
        this.mainFeature.changed();
    }

    updateReactiveProps()
    {
        this.reactiveProps.formattedArea = this.getFormattedArea();
    }

    clone(): Shape {
        const shape = new Shape(this.shapeCollection);
        shape.setCoordinates(this.getCoordinates());

        return shape;
    }

    serialize(): Serialization {
        const coordinates = [];
        for (const coordinate of this.getCoordinates()) {
            coordinates.push(toLonLat(coordinate));
        }
        return {
            name: this.name,
            color: this.color,
            lineStyleType: this.lineStyleType,
            fillInterior: this.fillInterior,
            coordinates: coordinates,
            visibleOnMap: this.visibleOnMap,
        };
    }

    static unserialize(serialized: Serialization, shapeCollection: ShapeCollection): Shape {
        const shape = new Shape(shapeCollection);

        shape.reactiveProps.name = shape.name = serialized.name;
        shape.setColor(serialized.color);
        shape.setLineStyleType(serialized.lineStyleType);
        shape.setFillInterior(serialized.fillInterior);
        shape.reactiveProps.visibleOnMap = shape.visibleOnMap = serialized.visibleOnMap;

        const coordinates: olCoordinate[] = [];
        for (const coordinate of serialized.coordinates) {
            coordinates.push(fromLonLat(coordinate));
        }
        shape.setCoordinates(coordinates);

        return shape;
    }

    public toggleVisibility(visible: boolean = null) {
        if (visible === null) {
            visible = !this.visibleOnMap;
        }

        this.reactiveProps.visibleOnMap = this.visibleOnMap = visible;

        if (!this.visibleOnMap && this.hasFocus()) {
            this.shapeCollection.unfocus();
        }

        this.mainFeature.changed();
    }

    public isVisible(): boolean {
        return this.visibleOnMap;
    }

    public mouseover(notifyLayer: boolean = true) {
        this.hasMouseover = true;
        $('#shape_' + this.id).addClass('hover');
        this.initStyles();
        if (notifyLayer) {
            this.mainFeature.changed();
        }
    }

    public mouseout(notifyLayer: boolean = true) {
        this.hasMouseover = false;
        $('#shape_' + this.id).removeClass('hover');
        this.initStyles();
        if (notifyLayer) {
            this.mainFeature.changed();
        }
    }

    public focus()
    {
        if (this.shapeCollection.getFocusedShape() !== this) {
            this.shapeCollection.focusShape(this);
        }
    }

    public hasFocus()
    {
        return this.reactiveProps.hasFocus;
    }

    public setFocus(focus)
    {
        if (!this.reactiveProps.hasFocus && focus) {
            if (this.shapeCollection.mainSource.hasFeature(this.mainFeature)) {
                this.shapeCollection.mainSource.removeFeature(this.mainFeature);
            }
            this.shapeCollection.modifySource.addFeature(this.mainFeature);

            this.applyFocus(true);
            this.reactiveProps.hasFocus = true;
            this.toggleVisibility(true);
            this.initStyles();
        } else if (this.reactiveProps.hasFocus && !focus) {
            if (this.shapeCollection.modifySource.hasFeature(this.mainFeature)) {
                this.shapeCollection.modifySource.removeFeature(this.mainFeature);
            }
            this.shapeCollection.mainSource.addFeature(this.mainFeature);

            this.applyFocus(false);
            this.setDrawMode(null);
            this.reactiveProps.hasFocus = false;
            this.modifyDragging = false; // Failsafe
            this.initStyles();
        }
    }

    applyFocus(newFocus: boolean): void
    {
        const olMap = this.shapeCollection.getOpenlayersMap();

        const oldFocus = this.reactiveProps.hasFocus;

        if (newFocus && !oldFocus) {
            olMap.addInteraction(this.modifyInteraction);
            olMap.addInteraction(this.drawInteraction);
        } else if (!newFocus && oldFocus) {
            this.setDrawMode(null);
            olMap.removeInteraction(this.modifyInteraction);
            olMap.removeInteraction(this.drawInteraction);
        }
    }

    getDrawMode(): ShapeDrawMode|null {
        return this.reactiveProps.drawMode;
    }

    setDrawMode(mode: ShapeDrawMode|null): void
    {
        this.reactiveProps.drawMode = mode;
    }

    handleKeyDown(evt: KeyboardEvent)
    {
        if (evt.defaultPrevented || !this.reactiveProps.hasFocus) {
            return;
        }

        if (evt.which === 27 || evt.code === 'Escape') {
            this.shapeCollection.unfocus();

            evt.preventDefault();
        }
    }

    getFormattedArea()
    {
        const geometry = <Polygon>this.mainFeature.getGeometry();
        const coordinates = geometry.getCoordinates();
        if (coordinates.length > 0 && coordinates[0].length > 3) {
            return Route.formatArea(getArea(geometry));
        }

        return null;
    }

    public drawOnPdf(doc: jsPDF, cutout: Cutout<any, any, any>)
    {
        Shape.drawShapeOnPdf(
            doc,
            cutout,
            this.getCoordinates(),
            this.color,
            this.lineStyleType,
            this.fillInterior,
        );
    }

    public static drawShapeOnPdf(
        doc: jsPDF,
        cutout: Cutout<any, any, any>,
        olCoordinates: olCoordinate[],
        hexColor: string,
        lineStyleType: LineStyleType,
        fillInterior: boolean,
    ) {
        const wgsSystem = <WGS84System>CoordinateConverter.getCoordinateSystem('EPSG:4326');
        const paperCoordinateConversion = cutout.getProjection().paperCoordinateConversion();
        const wgsToPaperCoord = new ConversionComposition(
            CoordinateConverter.conversion(wgsSystem, cutout.getProjection().coordinateSystem),
            paperCoordinateConversion
        );

        // Make sure we draw until outside the viewable area, such that strokes are only painted where we want those
        const paper = cutout.getPaper();
        const paperPolygonCoords = [
            [0, 0],
            [paper.width, 0],
            [paper.width, paper.height],
            [0, paper.height],
        ];

        const shapePolygon = olCoordinates.map(c => wgsToPaperCoord.convert(wgsSystem.fromOpenLayersCoordinate(c)));
        const shapePolygonCoords = shapePolygon.map(c => [c.getX(), c.getY()]);

        if (shapePolygonCoords.length < 3) {
            return;
        }

        const shapePaperPolygonCoords = polyclip.intersection([shapePolygonCoords], [paperPolygonCoords]);

        if (shapePaperPolygonCoords.length === 0 || shapePaperPolygonCoords[0].length === 0 || shapePaperPolygonCoords[0][0].length < 3) {
            return;
        }

        doc.saveGraphicsState();
        try {
            const color = asArray(hexColor || '#000000').slice(0);
            const lineDash = lineStyleType === 'dashed' ? [5, 3] : lineStyleType === 'dotted' ? [.5, .5] : null;
            const ctx = doc.context2d;

            for (const polygon of shapePaperPolygonCoords) {
                ctx.beginPath();

                for (const ring of polygon) { // [0]=exterior, [1..]=holes
                    ctx.moveTo(ring[0][0], ring[0][1]);
                    for (let i = 1; i < ring.length; i++) {
                        ctx.lineTo(ring[i][0], ring[i][1]);
                    }
                    ctx.closePath();
                }

                if (fillInterior) {
                    ctx.fillStyle = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
                    ctx.fill();
                }

                if (lineStyleType !== 'none') {
                    if (lineDash !== null) {
                        ctx.setLineDash(lineDash);
                    }

                    ctx.strokeStyle = 'rgb(0, 0, 0)';
                    ctx.lineWidth = 0.6;
                    ctx.stroke();

                    ctx.strokeStyle = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
                    ctx.lineWidth = 0.4;
                    ctx.stroke();
                }
            }
        } finally {
            doc.restoreGraphicsState();
        }
    }
}
